import axios from 'axios';
import NProgress from 'nprogress';

import { store } from '../store/store';
import router from '../router';
import eventBus from '@/EventBus';
import logReport from '@/util/logReport';
import { LOGIN } from '@/constants/errorCode';

import qs from 'qs';
import { getLoginApiUrl } from '@/constants/customInfo';

axios.defaults.baseURL = process.env.BASE_URL;
axios.defaults.paramsSerializer = (params) => {
  return qs.stringify(params, { arrayFormat: 'comma', encode: true, skipNulls: true });
};

axios.interceptors.request.use((config) => {
  if (config.url[0] === '/' && store.getters.getMenuPath) {
    config.headers['Url-Key'] = encodeURI(store.getters.getMenuPath);
  }

  setToken(config);

  if (config.NProgress !== false) {
    NProgress.start();
  }
  return config;
});

function isNeoWebRtcApi(url) {
  const webPhoneUrl = process.env.NEO_WEB_PHONE_URL;
  if (url.includes(webPhoneUrl)) return true;

  const trLinkUrl = process.env.NEO_WEB_RTC_TRLINK;
  if (url.includes(trLinkUrl)) return true;

  return false;
}

function setToken(config) {
  // 네오메카 webRTC 관련 api의 경우, 토큰 없이 호출
  if (isNeoWebRtcApi(config.url)) return;

  if (config.url.includes('auth/v2/refresh') || config.url.includes('auth/v2/access')) {
    config.headers.Authorization = `Bearer ${localStorage.getItem('refreshToken')}`;
  } else if (!config.url.includes('logout') && !config.url.includes(getLoginApiUrl())) {
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
  }
}

const networkErrorMessage = `네트워크가 원활하지 않아<br />사용자 요청을 완료하지 못했습니다.<br />잠시 후 다시 시도해주세요.`;

const alertMsg = (message, getButtonEvent = () => {}) => {
  eventBus.$emit('DialogPopup', {
    useDialog: true,
    messageHTML: `<dl class="txt"><dd>${message}</dd></dl>`,
    getButtonEvent
  });
};

const goToLogin = (res) => {
  if (res && router.app.$route.path !== '/member/login') {
    router.push('/member/login');
  }
};

axios.interceptors.response.use(
  (config) => {
    if (config.NProgress !== false) {
      NProgress.done();
    }
    return config;
  },
  (error) => {
    NProgress.done();
    // 카티니 커스텀 진행 시 > 카티니 open API 요청한 응답 메세지 처리해주기 위한 로직
    if (error.response && error.response.config && error.response.config.url && error.response.config.url.includes(getLoginApiUrl())) {
      return Promise.reject(error.response.data);
    }
    // 어떤 경우에 대비한 코드인지 확인 필요
    // 참조할 응답정보 없어서 프론트에서 보여줄 메세지가 없는 경우로 추측
    if (!error.response) {
      if (error.toString().indexOf('Network Error') > -1) {
        alertMsg(networkErrorMessage);
      }
      return Promise.reject(error);
    }

    // 이전 에러 응답 구조의 경우 여기를 타게 됨
    // 이전 {  code: '', message: '' } -> 신규 { error: { code: '', message: '' } }
    const data = error.response.data;
    const statusCode = error.response.status;
    if (!data.error) {
      // 이전 에러 응답 구조안에 메세지도 코드도 없는 상태
      // 어떤 경우에 대비한 코드인지 확인 필요
      const code = data.code;
      const message = data.message;
      if (!(message && code)) {
        alertMsg(`code : ${statusCode}<br />statusText : ${error.response.statusText}`);
      }

      if (message && code) {
        // TODO: 아래 음수로 되어있는 코드 값에 대한 로직들은 기존 로그인 구조이므로 변경해야 함.
        const loginExceptionCodeList = [-2021, -4, -8, -9, -10, -11];
        if (loginExceptionCodeList.indexOf(code) !== -1) {
          alertMsg(message);
        }
      }

      if (code && code === -502) {
        alertMsg(networkErrorMessage);
      }

      return Promise.reject(error);
    }

    // 신규 error 객체 구조의 경우 여기를 타게 됨
    if (data.error) {
      const error = data.error;
      const codeOfError = error.code;
      const messageOfError = error.message;

      // 로그인 관련 ealry return
      // 나머지는 모달 띄우기 또는 로그아웃 처리와 같은 추가 로직 존재
      if (
        [
          LOGIN.ACCOUNTS_NOT_FOUND,
          LOGIN.FAIL_TO_LOGIN,
          LOGIN.LOCKED_ACCOUNT,
          LOGIN.IS_NOT_LOGIN_PAGE,
          LOGIN.UNAUTHORIZED_ACCESS_IP,
          LOGIN.INVALID_OTP_REQUEST,
          LOGIN.NEED_OTP_AUTHENTICATION,
          LOGIN.NEED_OTP_EMAIL_CONFIRM,
          LOGIN.NEED_OTP_AUTHY_RESISTER,
          LOGIN.DUPLICATION_ACCESS_EXCEPTION,
          LOGIN.ACCOUNTS_PASSWORD_EXPIRED
        ].indexOf(codeOfError) > -1
      ) {
        return Promise.reject(error);
      }

      // http statusCode 401 (인증 안된 상태) 이면서, 토큰이 존재한다 (인증된 상태)
      if (statusCode === 401 && localStorage.getItem('token')) {
        // 에러 케이스 : 중복 로그인 시, 토큰 만료 되어있을 경우 강제 로그아웃 처리
        if (codeOfError === LOGIN.DUPLICATION_LOGIN_EXCEPTION || codeOfError === LOGIN.TOKEN_EXPIRED) {
          logReport.write(`[FORCE-LOGOUT] 자동 로그아웃 CODE:${codeOfError}`);
          store.dispatch('forceLogout').then((res) => {
            goToLogin(res);
          });
        } else {
          logReport.write(`[LOGOUT] CODE:${codeOfError}`);
          store.dispatch('logout').then((res) => {
            goToLogin(res);
          });
        }
        // 공통으로 뜨는 모달
        alertMsg(messageOfError);
      } else if (statusCode === 403 && localStorage.getItem('token')) {
        alertMsg(error.message, () => {
          router.push('/member/login');
        });
      }

      // 중복 로그인 불가 (강제 로그인 당하는 경우)
      const isValidToken = localStorage.getItem('isAuthorized') === 'true';
      if (codeOfError === LOGIN.DUPLICATION_ACCESS_EXCEPTION && isValidToken) {
        localStorage.setItem('isAuthorized', 'false');
        alertMsg(messageOfError);
      }
      // API response.data.error 객체 리턴
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export default axios;



// WEBPACK FOOTER //
// ./src/http/index.js